import { create } from 'zustand';

/**
 * ONE notice per circle across tabs (docs/plans/CIRCLE-STARTING-NOTICE.md §6
 * "Multi-tab", N5).
 *
 * What this browser already did with each circle's notice. A Not now or a Join
 * in any tab hides it in every tab at once:
 *
 *  - BroadcastChannel('ee-circle-notice') carries {event, circleId} to the
 *    other tabs now;
 *  - localStorage['ee.circle-notice'] ({[circleId]: outcome}) is the fallback
 *    (its `storage` event reaches the other tabs where the channel does not
 *    exist) and the record a fresh tab starts from;
 *  - without either (private mode, blocked storage) the session's memory is
 *    the fallback: this tab still never shows a notice twice.
 *
 * Signed in, the server rows back this up across DEVICES (rule 7); this is
 * only the tabs of one browser, which a 30 s poll cannot keep in step.
 */
export type CircleNoticeOutcome = 'shown' | 'clicked' | 'dismissed';

/**
 * Which notice an outcome belongs to: the circle id signed in, or a
 * signed-out key (`pub:hourly:<T>` / `pub:cohort:<id>:<T>`, PR #4), since the
 * public endpoint carries no circle id. Stored as the record's string keys.
 */
export type NoticeKey = number | string;

const SIGNED_OUT_KEY = /^pub:(hourly:\d{10}|cohort:\d{1,10}:\d{10})$/;

/** A key this module stores: a positive integer circle id or a signed-out key. */
export function isNoticeKey(k: unknown): k is NoticeKey {
  if (typeof k === 'number') return Number.isInteger(k) && k > 0;
  if (typeof k !== 'string') return false;
  if (SIGNED_OUT_KEY.test(k)) return true;
  return /^\d{1,10}$/.test(k) && Number(k) > 0;
}

export const CIRCLE_NOTICE_CHANNEL = 'ee-circle-notice';
export const CIRCLE_NOTICE_STORAGE_KEY = 'ee.circle-notice';

/** Outcomes that end the notice for that circle. */
export function endsNotice(outcome: CircleNoticeOutcome | undefined): boolean {
  return outcome === 'clicked' || outcome === 'dismissed';
}

const RANK: Record<CircleNoticeOutcome, number> = { shown: 0, clicked: 1, dismissed: 1 };

interface ChannelMessage {
  event: CircleNoticeOutcome;
  circleId: NoticeKey;
}

function readStorage(): Record<string, CircleNoticeOutcome> {
  try {
    const raw = window.localStorage.getItem(CIRCLE_NOTICE_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: Record<string, CircleNoticeOutcome> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (isNoticeKey(k) && (v === 'shown' || v === 'clicked' || v === 'dismissed')) {
        out[k] = v;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeStorage(outcomes: Record<string, CircleNoticeOutcome>): void {
  try {
    window.localStorage.setItem(CIRCLE_NOTICE_STORAGE_KEY, JSON.stringify(outcomes));
  } catch {
    /* memory is the fallback */
  }
}

interface CircleNoticeOutcomesState {
  outcomes: Record<string, CircleNoticeOutcome>;
  /** Record an outcome here and tell the other tabs. Never moves an ending outcome back. */
  mark: (circleId: NoticeKey, outcome: CircleNoticeOutcome) => void;
  /** An outcome another tab reported (channel or storage); not re-broadcast. */
  receive: (circleId: NoticeKey, outcome: CircleNoticeOutcome) => void;
}

let channel: BroadcastChannel | null = null;

function merge(
  prev: Record<string, CircleNoticeOutcome>,
  circleId: NoticeKey,
  outcome: CircleNoticeOutcome,
): Record<string, CircleNoticeOutcome> | null {
  const was = prev[circleId];
  if (was !== undefined && RANK[was] >= RANK[outcome]) return null;
  return { ...prev, [circleId]: outcome };
}

export const useCircleNoticeOutcomes = create<CircleNoticeOutcomesState>((set, get) => ({
  outcomes: typeof window === 'undefined' ? {} : readStorage(),
  mark: (circleId, outcome) => {
    const next = merge(get().outcomes, circleId, outcome);
    if (next === null) return;
    set({ outcomes: next });
    writeStorage(next);
    try {
      channel?.postMessage({ event: outcome, circleId } satisfies ChannelMessage);
    } catch {
      /* the storage event is the fallback */
    }
  },
  receive: (circleId, outcome) => {
    const next = merge(get().outcomes, circleId, outcome);
    if (next !== null) set({ outcomes: next });
  },
}));

function onChannelMessage(e: MessageEvent): void {
  const data = e.data as Partial<ChannelMessage> | null;
  if (!data || !isNoticeKey(data.circleId)) return;
  if (data.event !== 'shown' && data.event !== 'clicked' && data.event !== 'dismissed') return;
  useCircleNoticeOutcomes.getState().receive(data.circleId, data.event);
}

function onStorage(e: StorageEvent): void {
  if (e.key !== CIRCLE_NOTICE_STORAGE_KEY) return;
  const fromDisk = readStorage();
  for (const [id, outcome] of Object.entries(fromDisk)) {
    useCircleNoticeOutcomes.getState().receive(id, outcome);
  }
}

let listening = false;

/** Start listening to the other tabs (idempotent). Returns a stop for tests. */
export function listenToOtherTabs(): () => void {
  if (listening || typeof window === 'undefined') return () => {};
  listening = true;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CIRCLE_NOTICE_CHANNEL);
      channel.addEventListener('message', onChannelMessage);
    }
  } catch {
    channel = null;
  }
  window.addEventListener('storage', onStorage);
  return () => {
    listening = false;
    channel?.removeEventListener('message', onChannelMessage);
    channel?.close();
    channel = null;
    window.removeEventListener('storage', onStorage);
  };
}

/** Test seam. */
export function resetCircleNoticeOutcomes(): void {
  useCircleNoticeOutcomes.setState({ outcomes: {} });
}
